import { base64ToBytes, bytesToBase64, randomBytes, utf8ToBytes, bytesToUtf8 } from './bytes.ts';

export const PBKDF2_ITERATIONS = 210000;

export async function encryptRawBytes(
  key: CryptoKey,
  plaintext: Uint8Array
): Promise<{ iv: string; ciphertext: Uint8Array }> {
  const iv = randomBytes(12);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  return { iv: bytesToBase64(iv), ciphertext: new Uint8Array(ciphertext) };
}

export async function decryptRawBytes(
  key: CryptoKey,
  ivB64: string,
  ciphertext: Uint8Array
): Promise<Uint8Array> {
  const iv = base64ToBytes(ivB64);
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
  return new Uint8Array(plain);
}

export async function encryptBytes(key: CryptoKey, plaintext: Uint8Array): Promise<{ iv: string; ciphertext: string }> {
  const wrapped = await encryptRawBytes(key, plaintext);
  return { iv: wrapped.iv, ciphertext: bytesToBase64(wrapped.ciphertext) };
}

export async function decryptBytes(key: CryptoKey, ivB64: string, ciphertextB64: string): Promise<Uint8Array> {
  return decryptRawBytes(key, ivB64, base64ToBytes(ciphertextB64));
}

export async function encryptUtf8(key: CryptoKey, text: string): Promise<{ iv: string; ciphertext: string }> {
  return encryptBytes(key, utf8ToBytes(text));
}

export async function decryptUtf8(key: CryptoKey, ivB64: string, ciphertextB64: string): Promise<string> {
  return bytesToUtf8(await decryptBytes(key, ivB64, ciphertextB64));
}

export async function importAesKey(raw: Uint8Array, usages: KeyUsage[] = ['encrypt', 'decrypt']): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, usages);
}

export async function generateThreadKeyBytes(): Promise<Uint8Array> {
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  return new Uint8Array(await crypto.subtle.exportKey('raw', key));
}
